import { useRequiredContext } from "@redotech/react-util/context";
import { ANONYMOUS_SHOPPER_ID_KEY } from "@redotech/redo-web/utils/shared-conf";
import { collectAliaSubscriber } from "@redotech/shopify-sdk/rpc/schema/alia/collect-alia-subscriber";
import { memo, useEffect } from "react";
import { RedoShopifyRpcClientContext } from "../client/redo-shopify-rpc-client";
import { log } from "../monitoring/log";

interface AliaSignupEventDetail {
  type: string;
  email?: string;
  phone?: string;
  popupID?: string;
  campaignID?: string;
  campaignTitle?: string;
}

export const AliaEventListener = memo(function AliaEventListener() {
  const rpcClient = useRequiredContext(RedoShopifyRpcClientContext);

  useEffect(() => {
    log("Initializing Alia integration");

    const handleAliaSignup = async (event: Event) => {
      const customEvent = event as CustomEvent<AliaSignupEventDetail>;
      const { email, phone, popupID, campaignID, campaignTitle } =
        customEvent.detail;

      log("Alia signup event received", { email, phone, popupID, campaignID });

      if (!email && !phone) {
        log("Alia signup event missing email and phone", {
          popupID,
          campaignID,
        });
        return;
      }

      try {
        const anonymousId = localStorage.getItem(ANONYMOUS_SHOPPER_ID_KEY);

        const result = await rpcClient(collectAliaSubscriber, {
          email,
          phone,
          popupId: popupID,
          campaignId: campaignID,
          campaignTitle,
          anonymousId: anonymousId ?? undefined,
        });

        if (result.success) {
          log("Successfully collected Alia subscriber", {
            email,
            phone,
            popupID,
            campaignID,
          });
        } else {
          log("Failed to collect Alia subscriber", {
            email,
            phone,
            popupID,
            campaignID,
            error: result.error,
          });
        }
      } catch (error) {
        log("Error collecting Alia subscriber", error, {
          email,
          phone,
          popupID,
          campaignID,
        });
      }
    };

    document.addEventListener("alia:signup", handleAliaSignup);

    return () => {
      document.removeEventListener("alia:signup", handleAliaSignup);
    };
  }, [rpcClient]);

  return null;
});
